import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { computeTabOverflow, computeFitCount } from "./tabOverflow";

// InboxTabs — the ONE canonical tab style (Design Bible D16, CANON-SPECS §A + 5a
// /5j/5k). UNDERLINE tabs on BOTH breakpoints (the old mobile pill variant is
// discarded — 5k renders underline tabs on touch too):
//   • Active: text #1466D8, 2px #2283FF underline (margin-bottom:-1px so it
//     overlaps the row hairline). Inactive: #667085.
//   • Count is ALWAYS a bare number beside the label — NEVER a pill (§A). Active
//     count inherits the blue; inactive count is #1466D8 10.5px/700.
//   • Desktop: 12.5px/600, padding 10px 1px, abbreviated labels ("Agent Req.").
//     Mobile: 14px/600, padding 11px 1px, FULL labels ("Agent Requests"),
//     native swipe-scroll + a 44px right fade mask (5k). No More on touch (D17).
//   • Desktop tab overflow → Priority+ "More ▾" (D17, 5j): the active tab never
//     hides (it swaps into the last visible slot), and More carries the summed
//     unread of its hidden tabs. See tabOverflow.js for the pinned algorithm.
//
// Props:
//   tabs     [{ key, label, mobileLabel?, count? }] canonical order.
//   active   selected key.
//   onChange (key) => void
export const DEFAULT_INBOX_TABS = [
  { key: "dm", label: "DM" },
  { key: "last_minute", label: "Last Minute" },
  { key: "agent_requests", label: "Agent Req." },
  { key: "club_requests", label: "Club Req." },
  { key: "scout_requests", label: "Scout Req." },
];

// A single underline tab button (shared by the visible row + the mobile row).
// EXPORTED so secondary tab rows inside the inbox (e.g. the Requests gate's
// Received / Sent switch) render the ONE canonical D16 underline treatment
// instead of re-deriving it — underline = navigation, pill = filter/status.
export const TabButton = ({ tab, isActive, onChange, mobile }) => {
  const hasCount = typeof tab.count === "number" && tab.count > 0;
  const label = mobile && tab.mobileLabel ? tab.mobileLabel : tab.label;
  return (
    <button
      type="button"
      onClick={() => onChange?.(tab.key)}
      aria-current={isActive ? "page" : undefined}
      title={tab.mobileLabel || tab.label}
      className={
        "inline-flex flex-shrink-0 items-center gap-[5px] whitespace-nowrap border-b-2 bg-transparent " +
        (mobile
          ? "px-[1px] py-[11px] text-[14px] font-semibold "
          : "px-[1px] py-[10px] text-[12.5px] font-semibold ") +
        (isActive
          ? "-mb-px border-[#2283FF] text-[#1466D8]"
          : "border-transparent text-[#667085]")
      }
    >
      <span>{label}</span>
      {hasCount && (
        // Bare number — never a pill (CANON-SPECS §A). Active inherits blue text;
        // inactive tab still shows its count in #1466D8 10.5px/700.
        <span className="text-[10.5px] font-bold text-[#1466D8]">{tab.count}</span>
      )}
    </button>
  );
};

// More ▾ overflow control (desktop only, 5j). Renders the hidden tabs in a
// dropdown; picking one selects it (the parent's overflow recompute then swaps
// it into the visible row). Carries the aggregated hidden unread as a bare count.
const MoreTabsMenu = ({ hidden, hiddenCount, activeKey, onChange }) => {
  const [open, setOpen] = useState(false);
  if (!hidden || hidden.length === 0) return null;
  return (
    <div className="relative ml-auto flex-shrink-0" data-testid="inbox-more-tabs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-[5px] py-[10px] pl-2 text-[12.5px] font-semibold text-[#101828]"
      >
        More
        {hiddenCount > 0 && (
          <span className="text-[10.5px] font-bold text-[#1466D8]">{hiddenCount}</span>
        )}
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-1.5 overflow-hidden rounded-[10px] border border-[#E4E9F0] bg-white"
            style={{ minWidth: 208, boxShadow: "0 12px 32px rgba(16,24,40,.14)" }}
          >
            {hidden.map((tab, i) => {
              const isActive = tab.key === activeKey;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onChange?.(tab.key);
                    setOpen(false);
                  }}
                  className={
                    "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[12.5px] hover:bg-gray-50 " +
                    (i === 0 ? "bg-[#F8FAFC] " : "") +
                    (isActive ? "font-semibold text-[#1466D8]" : "text-[#101828]")
                  }
                >
                  <span>{tab.label}</span>
                  {typeof tab.count === "number" && tab.count > 0 && (
                    <span className="text-[10.5px] font-bold text-[#1466D8]">{tab.count}</span>
                  )}
                </button>
              );
            })}
            <div className="border-t border-[#F0F2F6]" />
            <p className="px-3 py-2 text-[10.5px] leading-snug text-[#98A2B3]">
              Picking one swaps it with the last visible tab — the active tab is never hidden in here.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Layout constants that MUST mirror the rendered row: the visible tab strip is
// `gap-[14px]`, so every inter-tab gap costs 14px of width — measuring the tabs
// without it makes the row look ~56px narrower than it renders (4 gaps for 5
// tabs) and the overflow check silently passes when the tabs actually clip.
const TAB_GAP = 14;
// Approximate width the "More ▾" control occupies (label + count + chevron)
// plus the gap before it. Only used once we KNOW we're overflowing.
const MORE_W = 64;

const InboxTabs = ({ tabs = DEFAULT_INBOX_TABS, active, onChange }) => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  // Number of tab slots (More button counts as one) that fit on desktop.
  // Infinity until measured (and always on mobile / where measurement is
  // unavailable) so every tab renders — overflow only ever HIDES tabs once
  // we've proven they don't fit.
  const [fitCount, setFitCount] = useState(Infinity);

  // Measure natural tab widths against the container to derive how many fit.
  // jsdom reports zero widths → fitCount stays Infinity → all tabs render (safe
  // for tests; the pure overflow algorithm is covered in tabOverflow.test.js).
  // Re-runs on every container resize (ResizeObserver) so shrinking the window
  // from lg (w-[392px]) into md (w-80 = 320px) pushes tabs into More live.
  useLayoutEffect(() => {
    if (isMobile) {
      setFitCount(Infinity);
      return undefined;
    }
    const measure = () => {
      const container = rowRef.current;
      const bench = measureRef.current;
      if (!container || !bench) return;
      const avail = container.clientWidth;
      const widths = Array.from(bench.children).map(
        (b) => b.getBoundingClientRect().width,
      );
      // Gap-aware slot count (pure, unit-tested in tabOverflow.test.js). Returns
      // Infinity when unmeasurable (jsdom) → all tabs render.
      setFitCount(computeFitCount(widths, avail, { gap: TAB_GAP, moreWidth: MORE_W }));
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined" && rowRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(rowRef.current);
    }
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isMobile, tabs]);

  // Mobile: full labels, native swipe-scroll, 44px right fade mask, no More.
  if (isMobile) {
    return (
      <div className="relative border-b border-[#F0F2F6]">
        <div
          className="flex items-stretch gap-[18px] overflow-x-auto"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {tabs.map((tab) => (
            <TabButton
              key={tab.key}
              tab={tab}
              isActive={tab.key === active}
              onChange={onChange}
              mobile
            />
          ))}
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-[44px]"
          style={{ background: "linear-gradient(90deg, rgba(255,255,255,0), #fff)" }}
          aria-hidden="true"
        />
      </div>
    );
  }

  // Desktop: Priority+ overflow.
  const { visible, hidden, hiddenCount } = computeTabOverflow(tabs, active, fitCount);

  return (
    <div className="relative border-b border-[#E4E9F0]">
      {/* Hidden measuring bench: all tabs at desktop styling, off-flow. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute -z-10 flex gap-[14px]"
        style={{ top: 0, left: 0 }}
      >
        {tabs.map((tab) => (
          <TabButton key={tab.key} tab={tab} isActive={false} onChange={undefined} />
        ))}
      </div>

      <div ref={rowRef} className="flex items-stretch gap-[14px]">
        {visible.map((tab) => (
          <TabButton
            key={tab.key}
            tab={tab}
            isActive={tab.key === active}
            onChange={onChange}
          />
        ))}
        <MoreTabsMenu
          hidden={hidden}
          hiddenCount={hiddenCount}
          activeKey={active}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

export default InboxTabs;
